import { useState, useCallback } from 'react';
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Grid from "@mui/material/Grid";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import LinearProgress from "@mui/material/LinearProgress";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import styles from "./styles";
import { Avatar, Box, Divider } from "@mui/material";
import Notifications from "../../features/notification-manager";
import CrewisorGuide from "../../features/crewisor-guide";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { withStyles } from 'tss-react/mui';
import apiUrl from '../../utils/apiUrl';
const anchorOrigin = {
  vertical: "top",
  horizontal: "right",
};

const transformOrigin = {
  vertical: "top",
  horizontal: "right",
};

const MenuAppBar = ({
  classes,
  username = null,
  logo,
  menuItems = null,
  tabs = null,
  navItems = null,
  currentTab = undefined,
  onTabChange = undefined,
  isLoading = false,
  avatar,
  notificationCount,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  // MUI's own breakpoint hook, not a static window.innerWidth read at
  // import time — the previous mobile check (navigation-bar/component)
  // never updated on resize/rotate. This one re-renders correctly.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const permissions = useSelector((state) =>
    get(state, "auth.data.permissions")
  );

  const handleOpenMenu = useCallback((e) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const linearClasses = {
    root: classes.linear,
    colorPrimary: classes.colorPrimary,
    barColorPrimary: classes.barColorPrimary,
  };

  return (
    <AppBar
      elevation={2}
      className={classes.root}
      position="sticky"
      color="inherit"
    >
      {isLoading && <LinearProgress classes={linearClasses} />}
      <Toolbar className={classes.bar}>
        {navItems && navItems.length > 0 && (
          // Below `sm`, the scrollable Tabs strip has to share the bar with
          // the logo + avatar cluster and ends up showing one truncated tab
          // behind two tiny scroll arrows — this replaces it with a proper
          // drawer instead of squeezing tabs into that leftover space.
          <IconButton
            onClick={openDrawer}
            aria-label="Open navigation menu"
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, mr: 0.5, flexShrink: 0 }}
          >
            <MenuRoundedIcon />
          </IconButton>
        )}
        <div className={classes.logo}>{logo}</div>
        {tabs && !isMobile && (
          <div className={classes.tabsSection}>
            <Tabs
              variant="scrollable"
              // "auto" hides the scroll arrows when content fits.
              // Default boolean `true` keeps them visible-but-disabled,
              // which left two greyed chevrons hanging in the bar
              // even when the row had room to spare.
              scrollButtons="auto"
              indicatorColor="primary"
              // MUI v5+ throws *"The `value` provided to the Tabs
              // component is invalid"* when the value doesn't match
              // any child Tab. Happens on /page-not-found, /403, etc.
              // — pass `false` to "no tab selected" instead.
              value={tabs.some((tab) => tab?.props?.value === currentTab) ? currentTab : false}
              onChange={onTabChange}
              allowScrollButtonsMobile>
              {tabs}
            </Tabs>
          </div>
        )}

        {/* Spacer used to be needed when tabsSection didn't flex.
            Now `tabsSection: flex 1` already pushes the user pill to
            the right — keep `grow` so layouts without tabs (or with
            tabs hidden for the mobile drawer) still right-align the
            user cluster. */}
        {(!tabs || isMobile) && <div className={classes.grow} />}

        {menuItems && (
          <ClickAwayListener onClickAway={handleMenuClose}>
            <div className={classes.userSection}>
              <Grid container direction="row" wrap="nowrap">
                <Grid className={classes.notificationsButton}>
                  {get(permissions, "showHeaderNotifications.enable") && (
                    <Notifications notificationCount={notificationCount} />
                  )}
                  {get(permissions, "showHeaderHelp.enable") && (
                    <CrewisorGuide />
                  )}
                </Grid>
                <Grid>
                  <Toolbar
                    className={classes.userInfo}
                    onClick={handleOpenMenu}
                  >
                    <Avatar alt={username} src={apiUrl(avatar)} />
                  </Toolbar>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={anchorOrigin}
                    keepMounted
                    transformOrigin={transformOrigin}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <Typography align="center" className={classes.userName}>
                      {username}
                    </Typography>
                    {menuItems}
                  </Menu>
                </Grid>
              </Grid>
            </div>
          </ClickAwayListener>
        )}
      </Toolbar>
      {navItems && navItems.length > 0 && (
        <Drawer anchor="left" open={drawerOpen} onClose={closeDrawer}>
          <Box sx={{ width: 264, pt: 2 }} role="presentation">
            <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center' }}>{logo}</Box>
            <Divider />
            <List sx={{ py: 1 }}>
              {navItems.map(({ key, value, label }) => (
                <ListItemButton
                  key={key}
                  component={Link}
                  to={`/${value}`}
                  selected={currentTab === value}
                  onClick={closeDrawer}
                >
                  <ListItemText primary={label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Drawer>
      )}
    </AppBar>
  );
};

MenuAppBar.propTypes = {
  classes: PropTypes.object.isRequired,
  username: PropTypes.string,
  logo: PropTypes.any.isRequired,
  menuItems: PropTypes.any,
  tabs: PropTypes.any,
  navItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string, value: PropTypes.string, label: PropTypes.string,
  })),
  currentTab: PropTypes.string,
  onTabChange: PropTypes.func,
  isLoading: PropTypes.bool,
  avatar: PropTypes.string,
  notificationCount: PropTypes.number,
};

export default withStyles(MenuAppBar, styles);
